export const ProjectStatus = {
  Bid: 10,
  DeclinedBidOp: 15,
  BidPending: 20,
  Unawarded: 25,
  Current: 30,
  DueDiligence: 40,
  Preproduction: 50,
  PreConstruction: 55,
  Production: 60,
  Construction: 65,
  Installation: 70,
  Completed: 80,
  FinalBilled: 93,
  ARReceivedAwaitingClosed: 96,
  Unbillable: 98,
  JobClosed: 100,
  Archived: 110,
} as const;

export const PROJECT_STATUS_LABELS: Record<number, string> = {
  10: "Bid",
  15: "Declined Bid Op",
  20: "Bid Pending",
  25: "Un-awarded",
  30: "Current",
  40: "Due Diligence",
  50: "Pre-production",
  55: "Pre-Construction",
  60: "Production",
  65: "Construction",
  70: "Installation",
  80: "Completed",
  93: "Final Billed",
  96: "A/R Received - Awaiting Closed",
  98: "Unbillable",
  100: "Job Closed",
  110: "Archived",
};

export const PROJECT_BID_STATUS_LABELS: Record<number, string> = {
  1: "Accepting Bids",
  2: "GC Bid Pending",
  3: "Closed",
};

export function projectStatusLabel(value: number | null | undefined): string {
  return value == null
    ? "—"
    : (PROJECT_STATUS_LABELS[value] ?? `Status ${value}`);
}

export function projectBidStatusLabel(
  value: number | null | undefined,
): string {
  return value == null
    ? "—"
    : (PROJECT_BID_STATUS_LABELS[value] ?? `Bid ${value}`);
}

/** Options for select inputs / filters, in enum order. */
export const PROJECT_STATUS_OPTIONS = Object.entries(PROJECT_STATUS_LABELS).map(
  ([value, label]) => ({
    value: Number(value),
    label,
  }),
);

export const PROJECT_BID_STATUS_OPTIONS = Object.entries(
  PROJECT_BID_STATUS_LABELS,
).map(([value, label]) => ({ value: Number(value), label }));

/**
 * Semantic tone for a status, used to color the status pill in the grid.
 * The 17 raw statuses collapse onto five lifecycle buckets so the table reads
 * as a small, legible palette rather than 17 arbitrary hues.
 */
export type ProjectStatusTone =
  | "active" // green — work in flight
  | "bidding" // indigo — pre-award
  | "closeout" // amber — winding down / billing
  | "preconstruction" // slate — awarded, not yet building
  | "neutral"; // gray — declined / on-hold / archived

const STATUS_TONE: Record<number, ProjectStatusTone> = {
  10: "bidding", // Bid
  20: "bidding", // Bid Pending
  15: "neutral", // Declined Bid Op
  25: "neutral", // Un-awarded
  98: "neutral", // Unbillable
  110: "neutral", // Archived
  40: "preconstruction", // Due Diligence
  50: "preconstruction", // Pre-production
  55: "preconstruction", // Pre-Construction
  30: "active", // Current
  60: "active", // Production
  65: "active", // Construction
  70: "active", // Installation
  80: "closeout", // Completed
  93: "closeout", // Final Billed
  96: "closeout", // A/R Received - Awaiting Closed
  100: "closeout", // Job Closed
};

export function projectStatusTone(
  value: number | null | undefined,
): ProjectStatusTone {
  return value == null ? "neutral" : (STATUS_TONE[value] ?? "neutral");
}

/**
 * Quick-filter shortcuts shown as segmented pills above the grid. Each maps to
 * a single backend status value (the list query filters on one status), so
 * these are the high-traffic statuses; the full 17-status set stays available
 * in the dropdown beside them. `undefined` = no status filter ("All").
 */
export const PROJECT_STATUS_QUICK_FILTERS: ReadonlyArray<{
  label: string;
  value: number | undefined;
}> = [
  { label: "All", value: undefined },
  { label: "Bid", value: ProjectStatus.Bid },
  { label: "Current", value: ProjectStatus.Current },
  { label: "Pre-Construction", value: ProjectStatus.PreConstruction },
  { label: "Completed", value: ProjectStatus.Completed },
  { label: "Job Closed", value: ProjectStatus.JobClosed },
];

/** Audit registry key for a project (`AuditScopes`) — used by the workspace History section. */
export const PROJECT_AUDIT_SCOPE = "project";
